import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/native';
import { Animated, Platform, View, type ViewStyle } from 'react-native';
import { BodyText, H2 } from '@/shared/components/StyledText';
import { accent, darkGreen, mint } from '@/shared/styles/colors';
import { IS_WEB } from '@/shared/constants/utility';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';
import {
  FONT_STYLES,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_MAX_WIDTH,
  WELCOME_TESTIMONIALS,
} from '@/shared/styles/constants';
import { borderRadius, spacing } from '@/shared/styles/constants';

interface GridLayoutProps {
  isWideGrid: boolean;
}

interface TestimonialNavArrowButtonProps {
  accessibilityLabel: string;
  onPress: () => void;
  onCarouselFocus: () => void;
  onCarouselBlur: () => void;
  children: React.ReactNode;
}

const ACCENT_HOVER = '#ebe86a';

const TestimonialNavArrowButton = ({
  accessibilityLabel,
  onPress,
  onCarouselFocus,
  onCarouselBlur,
  children,
}: TestimonialNavArrowButtonProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <ArrowButton
      $isFocused={isFocused}
      $isHovered={isHovered}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      onFocus={() => {
        setIsFocused(true);
        onCarouselFocus();
      }}
      onBlur={() => {
        setIsFocused(false);
        onCarouselBlur();
      }}
      hitSlop={WELCOME_TESTIMONIALS.hitSlop}
      {...webHoverProps}
    >
      {children}
    </ArrowButton>
  );
};

interface Testimonial {
  quote: string;
  name: string;
}

const quoteMotionLayoutStyle: ViewStyle = {
  width: '100%',
  alignItems: 'center',
};

const TESTIMONIALS: Testimonial[] = [
  {
    quote: 'The in-person service far exceeded my expectations.',
    name: 'Tripp',
  },
  {
    quote:
      "I'm like a kid on Christmas Day waiting to see what she comes up with for our next round.",
    name: 'Nicole',
  },
  {
    quote:
      'I received so many compliments and felt confident at every event. 10/10 experience.',
    name: 'Brenda',
  },
  {
    quote:
      'EMME took so much stress off my plate and replaced it with excitement and confidence',
    name: 'Carly',
  },
  {
    quote:
      'She encouraged me to step outside my comfort zone, and now getting dressed each day feels effortless.',
    name: 'Tori',
  },
  {
    quote:
      "EMME made hiring a stylist surprisingly accessible and seamless for a 'regular person' like me.",
    name: 'Sydney',
  },
];

const WelcomeClientTestimonialsSection = () => {
  const { isWide: isWideGrid } = useWelcomeSectionLayout();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHoveringQuote, setIsHoveringQuote] = useState(false);
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);
  const nativeFocusBlurTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const interactionHostRef = useRef<View | null>(null);
  const autoAdvanceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const opacityAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  const isPaused = isHoveringQuote || isFocusedWithin;
  const isAutoplayAllowed = !isPaused;

  const quoteHoverProps = useMemo(
    () =>
      Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringQuote(true);
          },
          onMouseLeave: () => {
            setIsHoveringQuote(false);
          },
        },
        default: {},
      }),
    [],
  );

  const count = TESTIMONIALS.length;
  const useNativeDriverForQuoteMotion = !IS_WEB;

  const skipQuoteEnterAnimationRef = useRef(true);

  useLayoutEffect(() => {
    if (skipQuoteEnterAnimationRef.current) {
      skipQuoteEnterAnimationRef.current = false;
      return;
    }
    if (prefersReducedMotion) {
      opacityAnim.setValue(1);
      slideAnim.setValue(0);
      return;
    }
    opacityAnim.stopAnimation();
    slideAnim.stopAnimation();
    opacityAnim.setValue(0);
    slideAnim.setValue(borderRadius.lg);
    Animated.parallel([
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: WELCOME_TESTIMONIALS.transitionMs,
        useNativeDriver: useNativeDriverForQuoteMotion,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: WELCOME_TESTIMONIALS.transitionMs,
        useNativeDriver: useNativeDriverForQuoteMotion,
      }),
    ]).start();
  }, [
    activeIndex,
    prefersReducedMotion,
    opacityAnim,
    slideAnim,
    useNativeDriverForQuoteMotion,
  ]);

  useEffect(() => {
    if (!IS_WEB || count <= 1) {
      return;
    }
    const node = interactionHostRef.current as unknown as HTMLElement | null;
    if (!node || typeof node.addEventListener !== 'function') {
      return;
    }
    const onFocusIn = () => {
      setIsFocusedWithin(true);
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      if (!next || !node.contains(next)) {
        setIsFocusedWithin(false);
      }
    };
    node.addEventListener('focusin', onFocusIn);
    node.addEventListener('focusout', onFocusOut);
    return () => {
      node.removeEventListener('focusin', onFocusIn);
      node.removeEventListener('focusout', onFocusOut);
    };
  }, [count]);

  const clearAutoAdvanceInterval = () => {
    if (autoAdvanceIntervalRef.current) {
      clearInterval(autoAdvanceIntervalRef.current);
      autoAdvanceIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (count <= 1) {
      return;
    }
    if (!isAutoplayAllowed) {
      return;
    }
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % count);
    }, WELCOME_TESTIMONIALS.autoAdvanceMs);
    autoAdvanceIntervalRef.current = id;
    return () => {
      clearInterval(id);
      if (autoAdvanceIntervalRef.current === id) {
        autoAdvanceIntervalRef.current = null;
      }
    };
  }, [count, isAutoplayAllowed]);

  const clearNativeFocusBlurTimeout = () => {
    if (nativeFocusBlurTimeoutRef.current) {
      clearTimeout(nativeFocusBlurTimeoutRef.current);
      nativeFocusBlurTimeoutRef.current = null;
    }
  };

  const onNativeCarouselControlFocus = () => {
    if (IS_WEB) {
      return;
    }
    clearNativeFocusBlurTimeout();
    setIsFocusedWithin(true);
  };

  const onNativeCarouselControlBlur = () => {
    if (IS_WEB) {
      return;
    }
    clearNativeFocusBlurTimeout();
    nativeFocusBlurTimeoutRef.current = setTimeout(() => {
      setIsFocusedWithin(false);
      nativeFocusBlurTimeoutRef.current = null;
    }, 0);
  };

  useEffect(() => {
    return () => {
      clearNativeFocusBlurTimeout();
    };
  }, []);

  const onPrevious = () => {
    if (count <= 1) {
      return;
    }
    clearAutoAdvanceInterval();
    setActiveIndex((prev) => (prev - 1 + count) % count);
  };

  const onNext = () => {
    if (count <= 1) {
      return;
    }
    clearAutoAdvanceInterval();
    setActiveIndex((prev) => (prev + 1) % count);
  };

  const item = TESTIMONIALS[activeIndex];

  const quoteCardInner = (
    <Card
      accessibilityLiveRegion={isFocusedWithin ? 'polite' : 'none'}
      accessibilityRole="none"
    >
      <QuoteMotionView
        style={[
          quoteMotionLayoutStyle,
          {
            opacity: opacityAnim,
            transform: [{ translateX: slideAnim }],
          },
        ]}
      >
        <QuoteText>&ldquo;{item.quote}&rdquo;</QuoteText>
        <AttributionText>— {item.name}</AttributionText>
      </QuoteMotionView>
    </Card>
  );

  const hasCarouselNav = count > 1;

  return (
    <Section>
      <SectionInner isWideGrid={isWideGrid}>
        <CarouselColumn>
          <SectionTitle>what our clients are saying...</SectionTitle>
          {hasCarouselNav ? (
            <CarouselInteractionZone {...quoteHoverProps}>
              <CardOuter>{quoteCardInner}</CardOuter>
              <CarouselNavHost ref={interactionHostRef}>
                <NavRow>
                  <TestimonialNavArrowButton
                    accessibilityLabel="Previous testimonial"
                    onPress={onPrevious}
                    onCarouselFocus={onNativeCarouselControlFocus}
                    onCarouselBlur={onNativeCarouselControlBlur}
                  >
                    <ArrowGlyph>←</ArrowGlyph>
                  </TestimonialNavArrowButton>
                  <TestimonialNavArrowButton
                    accessibilityLabel="Next testimonial"
                    onPress={onNext}
                    onCarouselFocus={onNativeCarouselControlFocus}
                    onCarouselBlur={onNativeCarouselControlBlur}
                  >
                    <ArrowGlyph>→</ArrowGlyph>
                  </TestimonialNavArrowButton>
                </NavRow>
              </CarouselNavHost>
            </CarouselInteractionZone>
          ) : (
            <CardOuter {...quoteHoverProps}>{quoteCardInner}</CardOuter>
          )}
        </CarouselColumn>
      </SectionInner>
    </Section>
  );
};

const Section = styled.View({
  width: '100%',
  backgroundColor: mint,
  paddingTop: WELCOME_TESTIMONIALS.sectionVerticalPadding,
  paddingBottom: WELCOME_TESTIMONIALS.sectionVerticalPadding,
});

const SectionInner = styled.View<GridLayoutProps>(({ isWideGrid }) => ({
  width: '100%',
  maxWidth: isWideGrid ? WELCOME_SECTION_MAX_WIDTH : undefined,
  alignSelf: 'center',
  paddingHorizontal: isWideGrid
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
}));

const CarouselColumn = styled.View({
  width: '100%',
  alignItems: 'center',
});

const CarouselInteractionZone = styled.View({
  width: '100%',
  alignItems: 'center',
});

const CarouselNavHost = styled.View({
  width: '100%',
  alignItems: 'center',
});

const SectionTitle = styled(H2)({
  color: darkGreen,
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: WELCOME_TESTIMONIALS.sectionTitleLetterSpacing,
  marginBottom: WELCOME_TESTIMONIALS.sectionTitleMarginBottom,
  alignSelf: 'stretch',
});

const CardOuter = styled.View({
  width: '100%',
  maxWidth: WELCOME_TESTIMONIALS.cardMaxWidth,
  marginBottom: WELCOME_TESTIMONIALS.quoteMarginBottom,
});

const Card = styled.View({
  width: '100%',
  paddingVertical: spacing.sm,
  paddingHorizontal: 0,
  alignItems: 'center',
});

const QuoteMotionView = Animated.createAnimatedComponent(View);

const QuoteText = styled(BodyText)({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: darkGreen,
  textAlign: 'center',
  fontStyle: 'italic',
  fontWeight: '300',
  fontSize: WELCOME_TESTIMONIALS.quoteFontSize,
  lineHeight: WELCOME_TESTIMONIALS.quoteLineHeight,
  marginBottom: WELCOME_TESTIMONIALS.quoteMarginBottom,
});

const AttributionText = styled(BodyText)({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: darkGreen,
  textAlign: 'center',
  fontWeight: '700',
  fontSize: WELCOME_TESTIMONIALS.attributionFontSize,
  lineHeight: WELCOME_TESTIMONIALS.attributionLineHeight,
  marginBottom: 0,
});

const NavRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: WELCOME_TESTIMONIALS.navRowGap,
});

interface ArrowButtonStyledProps {
  $isFocused?: boolean;
  $isHovered?: boolean;
}

const ArrowButton = styled.Pressable<ArrowButtonStyledProps>(
  ({ $isFocused, $isHovered }) => ({
    width: WELCOME_TESTIMONIALS.arrowCircleSize,
    height: WELCOME_TESTIMONIALS.arrowCircleSize,
    borderRadius: WELCOME_TESTIMONIALS.arrowCircleSize / 2,
    backgroundColor: $isHovered ? ACCENT_HOVER : accent,
    alignItems: 'center',
    justifyContent: 'center',
    ...(IS_WEB
      ? {
          outlineStyle: 'solid',
          outlineColor: darkGreen,
          outlineWidth: $isFocused ? 2 : 0,
          outlineOffset: $isFocused ? 2 : 0,
        }
      : $isFocused
        ? {
            shadowColor: darkGreen,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.95,
            shadowRadius: 5,
            elevation: 6,
          }
        : {}),
  }),
);

const ArrowGlyph = styled(BodyText)({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: darkGreen,
  fontSize: WELCOME_TESTIMONIALS.arrowGlyphFontSize,
  lineHeight: WELCOME_TESTIMONIALS.arrowGlyphLineHeight,
  fontWeight: '600',
});

export default WelcomeClientTestimonialsSection;
